"use client";

import { useMemo } from "react";

import {
    useFavoritePresets,
    usePresetParentCategories,
    usePresets,
    type PresetParentCategoryItem,
} from "@/hooks/use-presets";
import { mapPresetToV2Display } from "@/components/command-team-v2/utils/map-api-data";
import { mapPresetToProto } from "@/components/command-team-v2/utils/map-api-to-proto";
import type { ProtoPreset } from "@/components/command-team-v2/prototype/types";
import type { CommandPromptItem } from "@/components/aios-command/chat/command-prompts";

export type CommandV2PresetFilter =
    | "all"
    | "favourites"
    | "custom"
    | "executive"
    | "management";

export function parentCategoryId(
    items: PresetParentCategoryItem[],
    kind: "executive" | "management" | "custom"
): string | undefined {
    return items.find((item) => {
        const name = item.name.toUpperCase();
        if (kind === "executive") return name === "EXECUTIVE" || name === "SEE";
        if (kind === "custom") return name === "CUSTOM";
        return name === "MANAGEMENT" || name === "DO";
    })?.id;
}

function toProtoPresets(
    items: CommandPromptItem[],
    favoriteIds: Set<string>
): ProtoPreset[] {
    return items.map((item, index) =>
        mapPresetToProto(
            mapPresetToV2Display(item, {
                favourite: favoriteIds.has(item.id),
                order: index,
            })
        )
    );
}

export function useCommandV2EmptyPresets(filter: CommandV2PresetFilter) {
    const { data: parents, isLoading: isLoadingParents } = usePresetParentCategories();
    const parentItems = parents?.items ?? [];
    const executiveId = parentCategoryId(parentItems, "executive");
    const managementId = parentCategoryId(parentItems, "management");

    const allQuery = usePresets({});
    const customQuery = usePresets({ myPresets: true });
    const executiveQuery = usePresets({
        parentCategoryId: executiveId,
        enabled: Boolean(executiveId),
    });
    const managementQuery = usePresets({
        parentCategoryId: managementId,
        enabled: Boolean(managementId),
    });
    const favoritesQuery = useFavoritePresets({});

    const favoriteIds = favoritesQuery.favoriteIds;

    const lists = useMemo(
        () => ({
            all: toProtoPresets(allQuery.promptItems, favoriteIds),
            custom: toProtoPresets(customQuery.promptItems, favoriteIds),
            executive: toProtoPresets(executiveQuery.promptItems, favoriteIds),
            management: toProtoPresets(managementQuery.promptItems, favoriteIds),
            favourites: toProtoPresets(favoritesQuery.favoritePromptItems, favoriteIds),
        }),
        [
            allQuery.promptItems,
            customQuery.promptItems,
            executiveQuery.promptItems,
            managementQuery.promptItems,
            favoritesQuery.favoritePromptItems,
            favoriteIds,
        ]
    );

    const counts = useMemo(
        () => ({
            all: allQuery.totalCount,
            favourites: favoritesQuery.totalCount,
            custom: customQuery.totalCount,
            executive: executiveQuery.totalCount,
            management: managementQuery.totalCount,
        }),
        [
            allQuery.totalCount,
            favoritesQuery.totalCount,
            customQuery.totalCount,
            executiveQuery.totalCount,
            managementQuery.totalCount,
        ]
    );

    const active =
        filter === "all"
            ? allQuery
            : filter === "custom"
              ? customQuery
              : filter === "executive"
                ? executiveQuery
                : filter === "management"
                  ? managementQuery
                  : null;

    return {
        presets: lists[filter],
        counts,
        isLoading:
            ((filter === "executive" || filter === "management") && isLoadingParents) ||
            (filter === "favourites"
                ? favoritesQuery.isLoading
                : Boolean(active?.isLoading)),
        hasNextPage: filter === "favourites" ? false : Boolean(active?.hasNextPage),
        isFetchingNextPage:
            filter === "favourites" ? false : Boolean(active?.isFetchingNextPage),
        loadMoreRef: filter === "favourites" ? undefined : active?.loadMoreRef,
    };
}
